import { useEffect, useState, type CSSProperties } from 'react';
import {
  ASSET_TYPE_LABELS,
  EASY_SMS_RELAY_BASE_URL,
  POLL_INTERVAL_MS,
  SMS_PROVIDERS,
  SMS_PROVIDER_LABELS,
  toE164,
  type AlertPolicy,
  type NotificationGroup,
  type SmsProvider,
  type TenantSmsSettings,
} from '@ww/shared';
import { api, type AlertPolicyResponse, type SmsTestResponse } from '../api';
import { ErrorBox, Loading, PageHeader, Toggle, numOrNull } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { I, Icon, typeIcon } from '../lib/icons';
import { errorMessage, eventOptions, isAdmin, modeStyle } from '../lib/status';
import { fmtInterval } from '../lib/time';
import ListhubCard from '../components/ListhubCard';

type Tab = 'tenant' | 'global' | 'overrides' | 'groups' | 'sms' | 'listhub';
const TABS: [Tab, string][] = [
  ['tenant', 'Tenant policy'],
  ['global', 'Global defaults'],
  ['overrides', 'Asset overrides'],
  ['groups', 'Notification groups'],
  ['sms', 'SMS gateway'],
  ['listhub', 'ListHub'],
];

const rowCss: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 16px',
  borderBottom: '1px solid #18253c',
};

function newGroupId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `grp-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  }
}

function SmsTest({ admin, recipientHint }: { admin: boolean; recipientHint: string | null }) {
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState('');
  const [res, setRes] = useState<SmsTestResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!admin) return null;
  const trimmed = to.trim();
  // A test that quietly goes to every recipient is how a drill becomes a county-wide
  // page, so the destination is always spelled out before the button is pressed.
  const target = trimmed || recipientHint;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <label className="field" style={{ minWidth: 220 }}>
          <span className="lbl">Send test to (optional)</span>
          <input
            className="fld mono"
            placeholder={recipientHint ?? '+15551234567'}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn-secondary hov"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            setRes(null);
            try {
              setRes(await api.smsTest(trimmed || undefined));
            } catch (e) {
              setErr(errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Icon d={I.msg} size={16} />
          {busy ? 'Sending…' : 'Send test SMS'}
        </button>
      </div>
      <span className="muted" style={{ fontSize: 13.5 }}>
        {target
          ? `One message to ${target}. No other recipient is contacted.`
          : 'No recipients configured yet — type a number to test with.'}
      </span>
      {res && (
        <span role="status" style={{ fontSize: 14, color: res.result === 'failed' ? '#ff8f93' : '#6fdc98' }}>
          Result: <strong>{res.result}</strong>
          {res.detail ? ` · ${res.detail}` : ''}
        </span>
      )}
      {err && (
        <span role="alert" style={{ fontSize: 14, color: '#ff8f93' }}>
          {err}
        </span>
      )}
    </div>
  );
}

/**
 * Per-tenant SMS gateway. Each tenant sends with its own Easy SMS Relay API key, so a
 * revoked or exhausted key takes out one county rather than every customer.
 *
 * The key is write-only: the server never returns it, so the box is empty on load and an
 * empty box on save means "keep the installed key". That is why the draft key is tracked
 * separately from the settings being rendered.
 */
function SmsGatewayCard({ admin, recipients }: { admin: boolean; recipients: string[] }) {
  const [sms, setSms] = useState<TenantSmsSettings | null>(null);
  const [provider, setProvider] = useState<SmsProvider>('easysmsrelay');
  const [baseUrl, setBaseUrl] = useState(EASY_SMS_RELAY_BASE_URL);
  const [keyDraft, setKeyDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (next: TenantSmsSettings) => {
    setSms(next);
    setProvider(next.provider);
    setBaseUrl(next.baseUrl);
    setKeyDraft('');
  };

  useEffect(() => {
    let cancelled = false;
    api
      .smsSettings()
      .then((r) => {
        if (!cancelled) apply(r.sms);
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (body: Parameters<typeof api.updateSmsSettings>[0], message: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      apply((await api.updateSmsSettings(body)).sms);
      setNotice(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!sms && !error) return <Loading />;

  const dirty = !!sms && (provider !== sms.provider || baseUrl !== sms.baseUrl || keyDraft.trim().length > 0);
  const unusable = (recipients ?? []).filter((r) => !toE164(r));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {notice && (
        <div className="ok-box" role="status">
          {notice}
        </div>
      )}
      <ErrorBox error={error} />

      <section className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon d={I.msg} size={18} style={{ color: '#9fb0c8' }} />
          <strong style={{ fontSize: 15.5 }}>SMS gateway for this tenant</strong>
          {sms && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 13,
                fontWeight: 600,
                color: sms.configured ? '#6fdc98' : '#ffc36b',
              }}
            >
              {sms.configured ? `Key installed · ${sms.keyHint}` : 'No API key installed'}
            </span>
          )}
        </div>

        <label className="field">
          <span className="lbl">Provider</span>
          <select
            className="fld"
            disabled={!admin}
            value={provider}
            onChange={(e) => setProvider(e.target.value as SmsProvider)}
          >
            {SMS_PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {SMS_PROVIDER_LABELS[p]}
              </option>
            ))}
          </select>
        </label>

        {provider === 'easysmsrelay' && (
          <>
            <label className="field">
              <span className="lbl">API key</span>
              <input
                className="fld mono"
                type="password"
                autoComplete="off"
                disabled={!admin}
                placeholder={sms?.configured ? 'Installed — leave blank to keep it' : 'relay_…'}
                value={keyDraft}
                onChange={(e) => setKeyDraft(e.target.value)}
              />
            </label>
            <span className="muted" style={{ fontSize: 13.5 }}>
              Create a key with the <span className="mono">messages:send</span> scope on the Easy SMS Relay API keys
              page, then paste it here. It is stored for this tenant only and is never shown again.
            </span>
            <label className="field">
              <span className="lbl">Relay base URL</span>
              <input
                className="fld mono"
                disabled={!admin}
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </label>
          </>
        )}

        {provider === 'webhook' && (
          <span className="muted" style={{ fontSize: 13.5 }}>
            Posts the whole notification payload to the server-wide <span className="mono">SMS_WEBHOOK_URL</span>. No
            per-tenant key is used.
          </span>
        )}
        {provider === 'none' && (
          <span style={{ fontSize: 13.5, color: '#ffc36b' }}>
            Nothing is sent. Alerts are still evaluated and every notification is written to the log.
          </span>
        )}

        {admin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary hov"
              disabled={busy || !dirty}
              onClick={() =>
                void save(
                  {
                    provider,
                    baseUrl,
                    // Left out when blank: an empty box must never wipe a working key.
                    ...(keyDraft.trim() ? { apiKey: keyDraft.trim() } : {}),
                  },
                  keyDraft.trim() ? 'API key saved for this tenant.' : 'SMS gateway saved.',
                )
              }
            >
              {busy ? 'Saving…' : 'Save gateway'}
            </button>
            {dirty && (
              <button type="button" className="btn btn-ghost hov" onClick={() => sms && apply(sms)}>
                Discard
              </button>
            )}
            {sms?.configured && (
              <button
                type="button"
                className="btn btn-sm btn-danger hov"
                disabled={busy}
                style={{ marginLeft: 'auto' }}
                onClick={() => void save({ apiKey: null }, 'API key removed. This tenant can no longer send SMS.')}
              >
                Remove key
              </button>
            )}
          </div>
        )}

        {sms?.lastTestAt && (
          <span className="muted" style={{ fontSize: 13 }}>
            Last test {sms.lastTestResult ?? 'unknown'} at {new Date(sms.lastTestAt).toLocaleString()}
            {sms.lastTestDetail ? ` · ${sms.lastTestDetail}` : ''}
          </span>
        )}
        {sms?.updatedAt && (
          <span className="muted" style={{ fontSize: 13 }}>
            Changed {new Date(sms.updatedAt).toLocaleString()}
            {sms.updatedBy ? ` by ${sms.updatedBy}` : ''}
          </span>
        )}
      </section>

      {provider === 'easysmsrelay' && unusable.length > 0 && (
        <div className="warn-box" role="alert" style={{ fontSize: 13.5 }}>
          {unusable.length} recipient{unusable.length === 1 ? '' : 's'} cannot be texted by the relay, which needs a
          phone number (E.164, e.g. +15551234567): <span className="mono">{unusable.slice(0, 5).join(', ')}</span>
          {unusable.length > 5 ? ' …' : ''}. Fix them on the Notification groups tab.
        </div>
      )}

      <section className="panel" style={{ padding: 16 }}>
        <SmsTest admin={admin} recipientHint={recipients[0] ?? null} />
      </section>
    </div>
  );
}


export default function Rules() {
  const { me, assets, health, dashboard } = useLive();
  const admin = isAdmin(me?.user.role);
  const [tab, setTab] = useState<Tab>('tenant');
  const [data, setData] = useState<AlertPolicyResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AlertPolicy | null>(null);
  const [groups, setGroups] = useState<NotificationGroup[] | null>(null);
  const [recipientsText, setRecipientsText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const applyResponse = (r: AlertPolicyResponse) => {
    setData(r);
    setDraft(r.policy ? { ...r.policy } : null);
    const gs = Array.isArray(r.notificationGroups) ? r.notificationGroups : [];
    setGroups(gs.map((g) => ({ ...g, recipients: [...(g.recipients ?? [])] })));
    setRecipientsText(Object.fromEntries(gs.map((g) => [g.id, (g.recipients ?? []).join('\n')])));
  };

  useEffect(() => {
    let cancelled = false;
    api
      .alertPolicy()
      .then((r) => {
        if (!cancelled) applyResponse(r);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [dashboard?.tenant?._id]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const policy = data?.policy ?? null;
  const policyDirty = !!draft && !!policy && JSON.stringify(draft) !== JSON.stringify(policy);
  const serverGroups = data?.notificationGroups ?? [];
  const groupsForSave = (groups ?? []).map((g) => ({
    ...g,
    recipients: (recipientsText[g.id] ?? '')
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean),
  }));
  const groupsDirty = !!groups && JSON.stringify(groupsForSave) !== JSON.stringify(serverGroups);

  const savePolicy = async () => {
    if (!draft) return;
    if (draft.eventTypes.length === 0) return setError('Select at least one event type.');
    setBusy(true);
    setError(null);
    try {
      applyResponse(await api.updateAlertPolicy({ policy: draft }));
      setNotice('Alert policy saved. Change recorded in the audit log.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const saveGroups = async () => {
    if (groupsForSave.some((g) => !g.name.trim())) return setError('Every group needs a name.');
    setBusy(true);
    setError(null);
    try {
      applyResponse(await api.updateAlertPolicy({ notificationGroups: groupsForSave.map((g) => ({ ...g, name: g.name.trim() })) }));
      setNotice('Notification groups saved.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const setP = (patch: Partial<AlertPolicy>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const toggleIn = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const options = eventOptions([...(policy?.eventTypes ?? []), ...(policy?.alwaysAlert ?? [])]);
  const overrides = (assets ?? []).filter((a) => {
    const ro = a.ruleOverrides ?? {};
    const hasRo =
      Array.isArray(ro.eventTypes) ||
      (ro.windThresholdMph !== undefined && ro.windThresholdMph !== null) ||
      (ro.hailThresholdIn !== undefined && ro.hailThresholdIn !== null);
    return hasRo || (policy ? a.alertRadiusMiles !== policy.defaultRadiusMiles : false);
  });
  const groupName = (id: string) => serverGroups.find((g) => g.id === id)?.name ?? id;
  const currentMode = health?.polling?.mode;

  const fmtPolicy = (p: AlertPolicy | null | undefined): [string, string][] =>
    p
      ? [
          ['Notifying event types', p.eventTypes.join(', ') || '—'],
          ['Always alert', p.alwaysAlert.join(', ') || '—'],
          ['Wind threshold', p.windThresholdMph === null ? 'Off (event class only)' : `${p.windThresholdMph} mph`],
          ['Hail threshold', p.hailThresholdIn === null ? 'Off (event class only)' : `${p.hailThresholdIn.toFixed(2)} in`],
          ['Default asset radius', `${p.defaultRadiusMiles} mi`],
          ['Reminder cooldown', `${p.cooldownMinutes} min`],
          ['Notify on material change', p.notifyOnMaterialChange ? 'Yes' : 'No'],
          ['SMS enabled', p.smsEnabled ? 'Yes' : 'No'],
          ['Fastest NWS poll', fmtInterval(POLL_INTERVAL_MS.alert)],
          ['Dedupe key', 'NWS alert ID + material-change hash'],
        ]
      : [];

  return (
    <div className="page page-scroll" style={{ gap: 14 }}>
      <PageHeader
        title="Alert rules"
        sub="Global defaults, then tenant rules, then asset overrides, then notification group subscriptions"
      >
        {admin && tab === 'tenant' && policyDirty && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, color: '#ffc36b' }}>Unsaved changes</span>
            <button type="button" className="btn btn-ghost hov" onClick={() => policy && setDraft({ ...policy })}>
              Discard
            </button>
            <button type="button" className="btn btn-primary hov" disabled={busy} onClick={() => void savePolicy()}>
              {busy ? 'Saving…' : 'Save policy'}
            </button>
          </div>
        )}
        {admin && tab === 'groups' && groupsDirty && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 14, color: '#ffc36b' }}>Unsaved changes</span>
            <button type="button" className="btn btn-ghost hov" onClick={() => data && applyResponse(data)}>
              Discard
            </button>
            <button type="button" className="btn btn-primary hov" disabled={busy} onClick={() => void saveGroups()}>
              {busy ? 'Saving…' : 'Save groups'}
            </button>
          </div>
        )}
      </PageHeader>

      <div role="tablist" className="tabs" style={{ flexShrink: 0 }}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" className="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {notice && (
        <div className="ok-box" role="status">
          {notice}
        </div>
      )}
      <ErrorBox error={error} />
      {loadError && <ErrorBox error={`Could not load alert policy: ${loadError}`} />}
      {!data && !loadError && <Loading />}

      {data && draft && tab === 'tenant' && (
        <div className="rules-grid">
          <section className="panel">
            {!admin && (
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2c46', fontSize: 13.5, color: '#9fb0c8' }}>
                Read only. Tenant admins can change these rules.
              </div>
            )}
            <div style={{ padding: '12px 16px 4px' }} className="lbl">
              Event types
            </div>
            <div style={{ ...rowCss, padding: '0 16px 6px', gap: 0 }} className="lbl">
              <span style={{ flexGrow: 1 }} />
              <span style={{ width: 96, textAlign: 'center' }}>Notify</span>
              <span style={{ width: 96, textAlign: 'center' }}>Always</span>
            </div>
            {options.map((ev) => {
              const on = draft.eventTypes.includes(ev);
              const always = draft.alwaysAlert.includes(ev);
              return (
                <div key={ev} style={{ ...rowCss, padding: '8px 16px', gap: 0 }}>
                  <span style={{ flexGrow: 1, fontSize: 15, fontWeight: 600 }}>{ev}</span>
                  <span style={{ width: 96, display: 'flex', justifyContent: 'center' }}>
                    <Toggle
                      on={on}
                      label={`Notify for ${ev}`}
                      onChange={
                        admin
                          ? (v) =>
                              setP({
                                eventTypes: toggleIn(draft.eventTypes, ev),
                                alwaysAlert: v ? draft.alwaysAlert : draft.alwaysAlert.filter((x) => x !== ev),
                              })
                          : undefined
                      }
                    />
                  </span>
                  <span style={{ width: 96, display: 'flex', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`Always alert for ${ev}`}
                      checked={always}
                      disabled={!admin || !on}
                      onChange={() => setP({ alwaysAlert: toggleIn(draft.alwaysAlert, ev) })}
                      style={{ width: 18, height: 18, accentColor: '#2f7de1' }}
                    />
                  </span>
                </div>
              );
            })}
            <div style={{ padding: '10px 16px', fontSize: 12.5, color: '#9fb0c8' }}>
              “Always” events notify whenever the county or any enabled asset is affected, regardless of wind/hail thresholds.
            </div>
          </section>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <section className="panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span className="lbl">Severe thunderstorm thresholds</span>
              {(
                [
                  ['windThresholdMph', 'Damaging wind', 'mph', 5, 50],
                  ['hailThresholdIn', 'Large hail', 'in', 0.25, 1],
                ] as const
              ).map(([key, label, unit, step, dflt]) => {
                const v = draft[key];
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Toggle
                      on={v !== null}
                      label={`Use ${label.toLowerCase()} threshold`}
                      onChange={admin ? (on) => setP({ [key]: on ? policy?.[key] ?? dflt : null }) : undefined}
                    />
                    <span style={{ flexGrow: 1, fontSize: 14.5 }}>{label}</span>
                    <input
                      className="fld mono"
                      type="number"
                      min={0}
                      step={step}
                      disabled={!admin || v === null}
                      aria-label={`${label} threshold (${unit})`}
                      value={v ?? ''}
                      onChange={(e) => setP({ [key]: numOrNull(e.target.value) })}
                      style={{ width: 96 }}
                    />
                    <span className="muted" style={{ width: 30, fontSize: 13 }}>
                      {unit}
                    </span>
                  </div>
                );
              })}
              <span style={{ fontSize: 12.5, color: '#9fb0c8' }}>
                Off = any Severe Thunderstorm Warning qualifies (event class only).
              </span>
            </section>

            <section className="panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span className="lbl">Noise control</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexGrow: 1, fontSize: 14.5 }}>Reminder cooldown (min)</span>
                <input
                  className="fld mono"
                  type="number"
                  min={0}
                  disabled={!admin}
                  value={draft.cooldownMinutes}
                  onChange={(e) => setP({ cooldownMinutes: numOrNull(e.target.value) ?? 0 })}
                  style={{ width: 96 }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexGrow: 1, fontSize: 14.5 }}>Default radius for new assets (mi)</span>
                <input
                  className="fld mono"
                  type="number"
                  min={0}
                  max={100}
                  disabled={!admin}
                  value={draft.defaultRadiusMiles}
                  onChange={(e) => setP({ defaultRadiusMiles: numOrNull(e.target.value) ?? 0 })}
                  style={{ width: 96 }}
                />
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexGrow: 1, fontSize: 14.5 }}>
                  Notify on material change
                  <br />
                  <span style={{ fontSize: 12.5, color: '#9fb0c8' }}>
                    Severity, polygon, expiration, wind/hail or affected assets
                  </span>
                </span>
                <Toggle
                  on={draft.notifyOnMaterialChange}
                  label="Notify on material change"
                  onChange={admin ? (v) => setP({ notifyOnMaterialChange: v }) : undefined}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexGrow: 1, fontSize: 14.5 }}>
                  SMS notifications
                  <br />
                  <span style={{ fontSize: 12.5, color: '#9fb0c8' }}>Off = dashboard only</span>
                </span>
                <Toggle on={draft.smsEnabled} label="SMS enabled" onChange={admin ? (v) => setP({ smsEnabled: v }) : undefined} />
              </div>
            </section>

            <section className="panel" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="lbl" style={{ marginBottom: 4 }}>
                Adaptive NWS polling
              </span>
              {(
                [
                  ['normal', 'No severe alert affecting monitored areas'],
                  ['heightened', 'Watch or lower-priority event in monitored area'],
                  ['alert', 'Qualifying warning affects county or asset'],
                  ['recovery', 'Alerts expired or cancelled (10 min)'],
                ] as const
              ).map(([mode, trig]) => {
                const on = currentMode === mode;
                const ms = modeStyle(mode);
                return (
                  <div
                    key={mode}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '100px minmax(0, 1fr) auto',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 10px',
                      borderRadius: 7,
                      background: on ? '#16294a' : 'transparent',
                      boxShadow: on ? 'inset 0 0 0 1px #2b4574' : undefined,
                    }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 14.5, fontWeight: 600, color: ms.color }}>{ms.name.replace(' mode', '')}</span>
                      {on && <span style={{ fontSize: 11.5, color: '#8cc2ff' }}>Current</span>}
                    </span>
                    <span style={{ fontSize: 12.5, color: '#9fb0c8', lineHeight: 1.35 }}>{trig}</span>
                    <span className="mono" style={{ fontSize: 13 }}>
                      {fmtInterval(POLL_INTERVAL_MS[mode])}
                    </span>
                  </div>
                );
              })}
              <span style={{ fontSize: 12.5, color: '#9fb0c8', marginTop: 4 }}>
                Never faster than 30 s, per NWS guidance. Managed by the platform.
              </span>
            </section>
          </div>
        </div>
      )}

      {data && tab === 'global' && (
        <section className="panel" style={{ maxWidth: 720 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e2c46', fontSize: 14, color: '#9fb0c8' }}>
            Read only. Global defaults are managed by the super admin and apply to every tenant unless overridden.
          </div>
          {fmtPolicy(data.globalDefaults).map(([k, v]) => (
            <div
              key={k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 20,
                padding: '12px 16px',
                borderBottom: '1px solid #18253c',
                fontSize: 15,
              }}
            >
              <span style={{ color: '#c2cedf' }}>{k}</span>
              <span style={{ fontWeight: 600, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </section>
      )}

      {data && tab === 'overrides' && (
        <section className="panel" style={{ overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Type</th>
                <th>Radius</th>
                <th>Overrides</th>
                <th>Groups</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {!assets && (
                <tr>
                  <td colSpan={6} className="muted">
                    Loading…
                  </td>
                </tr>
              )}
              {assets && overrides.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No assets override the tenant policy.
                  </td>
                </tr>
              )}
              {overrides.map((a) => {
                const ro = a.ruleOverrides ?? {};
                const parts: string[] = [];
                if (Array.isArray(ro.eventTypes)) parts.push(`Events: ${ro.eventTypes.join(', ') || 'none'}`);
                if (ro.windThresholdMph !== undefined && ro.windThresholdMph !== null) parts.push(`Wind ≥ ${ro.windThresholdMph} mph`);
                if (ro.hailThresholdIn !== undefined && ro.hailThresholdIn !== null) parts.push(`Hail ≥ ${ro.hailThresholdIn} in`);
                return (
                  <tr key={a._id}>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14.5 }}>
                        <Icon d={typeIcon(a.type)} size={18} style={{ color: '#9fb0c8' }} />
                        {a.name}
                      </span>
                    </td>
                    <td>{ASSET_TYPE_LABELS[a.type] ?? a.type}</td>
                    <td className="mono">
                      {a.alertRadiusMiles} mi
                      {policy && a.alertRadiusMiles !== policy.defaultRadiusMiles ? (
                        <span className="muted"> (default {policy.defaultRadiusMiles})</span>
                      ) : null}
                    </td>
                    <td>{parts.length ? parts.join(' · ') : <span className="muted">Radius only</span>}</td>
                    <td>{(a.notificationGroupIds ?? []).map(groupName).join(', ') || <span className="muted">All groups</span>}</td>
                    <td className="muted">{a.enabled ? 'Enabled' : 'Disabled'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {data && groups && tab === 'groups' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="muted" style={{ fontSize: 14, flexGrow: 1 }}>
              Recipients are phone numbers, one per line or comma-separated. Easy SMS Relay needs E.164
              (+15551234567); a plain 10-digit US number is accepted and normalised. The gateway itself lives on
              the SMS gateway tab.
            </span>
          </div>
          <div className="cards-4">
            {groups.map((g) => {
              const assetCount = (assets ?? []).filter((a) => (a.notificationGroupIds ?? []).includes(g.id)).length;
              return (
                <section key={g.id} className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label className="field">
                    <span className="lbl">Group name</span>
                    <input
                      className="fld"
                      disabled={!admin}
                      value={g.name}
                      onChange={(e) =>
                        setGroups((gs) => (gs ?? []).map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x)))
                      }
                      style={{ fontWeight: 600 }}
                    />
                  </label>
                  <label className="field">
                    <span className="lbl">Recipients</span>
                    <textarea
                      className="fld mono"
                      rows={4}
                      disabled={!admin}
                      value={recipientsText[g.id] ?? ''}
                      onChange={(e) => setRecipientsText((r) => ({ ...r, [g.id]: e.target.value }))}
                      style={{ fontSize: 13 }}
                    />
                  </label>
                  <span style={{ fontSize: 13.5, color: '#9fb0c8' }}>
                    {assetCount} asset{assetCount === 1 ? '' : 's'} subscribed
                  </span>
                  {admin && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger hov"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => setGroups((gs) => (gs ?? []).filter((x) => x.id !== g.id))}
                    >
                      Remove group
                    </button>
                  )}
                </section>
              );
            })}
            {admin && (
              <button
                type="button"
                className="hov"
                onClick={() => {
                  const id = newGroupId();
                  setGroups((gs) => [...(gs ?? []), { id, name: 'New group', recipients: [] }]);
                  setRecipientsText((r) => ({ ...r, [id]: '' }));
                }}
                style={{
                  minHeight: 160,
                  borderRadius: 10,
                  border: '1px dashed #37598f',
                  background: 'transparent',
                  color: '#8cc2ff',
                  fontSize: 15,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <Icon d={I.plus} size={18} /> Add group
              </button>
            )}
          </div>
          {groups.length === 0 && !admin && <div className="empty">No notification groups configured.</div>}
        </div>
      )}

      {tab === 'sms' && (
        <SmsGatewayCard
          admin={admin}
          recipients={serverGroups.flatMap((g) => g.recipients ?? [])}
        />
      )}

      {tab === 'listhub' && <ListhubCard admin={admin} />}
    </div>
  );
}
